import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ThemeProvider } from "@/theme/ThemeContext";
import { isAdmin } from "@/lib/access";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Starters from "@/pages/Starters";
import Onboarding from "@/pages/Onboarding";
import Settings from "@/pages/Settings";
import Home from "@/pages/Home";
import Calendar from "@/pages/Calendar";
import CreateChronicle from "@/pages/CreateChronicle";
import Assistant from "@/pages/Assistant";
import MonthOverview from "@/pages/MonthOverview";
import Tasks from "@/pages/Tasks";
import CreateTask from "@/pages/CreateTask";
import AdminDashboard from "@/pages/AdminDashboard";
import Palvelumme from "@/pages/Palvelumme";
import MigrationDialog from "@/components/MigrationDialog";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );
}

// Wraps the main application pages: requires auth + completed Starters.
function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.startersCompleted) return <Navigate to="/starters" replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user && user !== false) {
    if (!user.onboardingCompleted && !user.startersCompleted) return <Navigate to="/onboarding" replace />;
    return <Navigate to={user.startersCompleted ? "/" : "/starters"} replace />;
  }
  return <Login />;
}

// One-time intro onboarding. Requires auth; can be reopened anytime via the Home "Guide" button.
function OnboardingRoute() {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Onboarding />;
}

function StartersRoute() {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.onboardingCompleted && !user.startersCompleted) return <Navigate to="/onboarding" replace />;
  if (user.startersCompleted) return <Navigate to="/" replace />;
  return <Starters />;
}

// Admin/Founder-only route. Normal users are redirected home.
function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.startersCompleted) return <Navigate to="/starters" replace />;
  if (!isAdmin(user)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/starters" element={<StartersRoute />} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/palvelumme" element={<Protected><Palvelumme /></Protected>} />
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="/calendar/:date" element={<Protected><Calendar /></Protected>} />
      <Route path="/create" element={<Protected><CreateChronicle /></Protected>} />
      <Route path="/create/:date" element={<Protected><CreateChronicle /></Protected>} />
      <Route path="/edit/:id" element={<Protected><CreateChronicle /></Protected>} />
      <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
      <Route path="/month" element={<Protected><MonthOverview /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/tasks/new" element={<Protected><CreateTask /></Protected>} />
      <Route path="/tasks/edit/:id" element={<Protected><CreateTask /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background text-foreground">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <MigrationDialog />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
